import React, { useState } from "react";
import { Skeleton } from "antd";
import { VideoCameraOutlined } from "@ant-design/icons";
import PopoverToolbar from "../EbulletinPopoverToolbar";
import UploadCard from "../EbulletinUploadCard";
import UploadModal from "../modals/EbulletinUploadModal";

export default function VideoBlock({
  block,
  isEditing,
  updateBlockProperty,
  bulletinId,
  sectionId,
}) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);

  // Parse block content if it's JSON (for media blocks)
  const getVideoData = () => {
    if (block.url) {
      // New format - direct properties
      return {
        url: block.url,
        title: block.title || "",
        thumbnail: block.thumbnail || "",
        style: block.style || {},
      };
    } else if (block.content) {
      try {
        // Try to parse as JSON first (serialized media data)
        const parsed = JSON.parse(block.content);
        if (parsed.url) {
          return parsed;
        }
        // Fallback: treat content as direct URL
        return {
          url: block.content,
          title: "",
          thumbnail: "",
          style: {},
        };
      } catch (e) {
        // Not JSON, treat as direct URL
        return {
          url: block.content,
          title: "",
          thumbnail: "",
          style: {},
        };
      }
    }
    return { url: "", title: "", thumbnail: "", style: {} };
  };

  const videoData = getVideoData();
  const videoUrl = videoData.url;

  // If no video URL, show a placeholder or skeleton
  const handleUploadFinish = (uploadResult) => {
    try {
      let newVideoUrl, fileName, fileSize, mimeType;

      if (typeof uploadResult === "string") {
        newVideoUrl = uploadResult;
      } else {
        newVideoUrl = uploadResult.dataUrl || uploadResult.url;
        fileName = uploadResult.fileName || uploadResult.name;
        fileSize = uploadResult.fileSize || uploadResult.size;
        mimeType = uploadResult.mimeType || uploadResult.type;
      }

      // Update all video properties
      updateBlockProperty("url", newVideoUrl);
      updateBlockProperty("title", videoData.title || fileName || "Video");
      updateBlockProperty("thumbnail", videoData.thumbnail);

      // Store additional metadata if available
      if (fileName) updateBlockProperty("fileName", fileName);
      if (fileSize) updateBlockProperty("fileSize", fileSize);
      if (mimeType) updateBlockProperty("mimeType", mimeType);

      setIsModalVisible(false);
    } catch (error) {
      console.error("Video upload error:", error);
    }
  };

  // Handle title changes
  const handleTitleChange = (title) => {
    updateBlockProperty("title", title);
  };

  // Render the video element based on the URL
  const renderVideoElement = () => {
    if (videoUrl.includes("youtube.com") || videoUrl.includes("youtu.be")) {
      // Extract video ID
      let videoId = "";
      let startTime = "";

      if (videoUrl.includes("watch?v=")) {
        const url = new URL(videoUrl);
        videoId = url.searchParams.get("v") || "";
        const tParam = url.searchParams.get("t");
        if (tParam) {
          // Convert t=28s or t=28 to seconds
          const match = tParam.match(/(\d+)s?/);
          if (match) {
            startTime = `?start=${match[1]}`;
          }
        }
      } else if (videoUrl.includes("youtu.be/")) {
        const match = videoUrl.match(/youtu\.be\/([^?]+)/);
        if (match) {
          videoId = match[1];
        }
        const url = new URL(videoUrl);
        const tParam = url.searchParams.get("t");
        if (tParam) {
          const match = tParam.match(/(\d+)s?/);
          if (match) {
            startTime = `?start=${match[1]}`;
          }
        }
      }

      const embedUrl = `https://www.youtube.com/embed/${videoId}${startTime}`;

      return (
        <iframe
          src={embedUrl}
          title={videoData.title || "Embedded Video"}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={videoData.style}
        />
      );
    } else if (videoUrl.includes("drive.google.com")) {
      // Google Drive video
      const embedUrl = (() => {
        const match = videoUrl.match(/\/file\/d\/([^/]+)/);
        if (match) {
          return `https://drive.google.com/file/d/${match[1]}/preview`;
        }
        const altMatch = videoUrl.match(/id=([^&]+)/);
        if (altMatch) {
          return `https://drive.google.com/file/d/${altMatch[1]}/preview`;
        }
        return videoUrl;
      })();

      return (
        <iframe
          src={embedUrl}
          title={videoData.title || "Google Drive Video"}
          allow="autoplay"
          allowFullScreen
          style={videoData.style}
        />
      );
    } else {
      return (
        <video
          src={videoUrl}
          controls
          className="uploaded-video"
          style={videoData.style}
          title={videoData.title}
          onError={(e) => {
            console.error("Video failed to load:", videoUrl);
          }}
        />
      );
    }
  };

  const videoElement = (
    <div className="video-wrapper">
      <div className="embed-video-wrapper">{renderVideoElement()}</div>
    </div>
  );

  return (
    <div className="block-card video">
      <UploadModal
        type="video"
        visible={isModalVisible}
        setVisible={setIsModalVisible}
        onUploadFinish={handleUploadFinish}
        bulletinId={bulletinId}
        sectionId={sectionId}
      />

      {!videoUrl ? (
        isEditing ? (
          <UploadCard type="video" onClick={() => setIsModalVisible(true)} />
        ) : (
          <div className="video-upload-placeholder">
            <Skeleton.Node active />
            <VideoCameraOutlined className="placeholder-icon" />
          </div>
        )
      ) : (
        <div>
          {isEditing ? (
            <PopoverToolbar
              showPopover={isPopoverVisible}
              onOpenChange={setIsPopoverVisible}
              showUploadControls={true}
              showVideoControls={true}
              currentTitle={videoData.title || ""}
              onUploadClick={() => {
                setIsModalVisible(true);
                setIsPopoverVisible(false);
              }}
              onTitleChange={handleTitleChange}
            >
              {videoElement}
            </PopoverToolbar>
          ) : (
            videoElement
          )}
        </div>
      )}
    </div>
  );
}
